/**
 * <RichText> — du markdown + LaTeX rendu comme l'élève le voit (molecule, web).
 *
 * Porte `tutor/home/MarkdownWithLatex.web.js` (et sa feuille `MarkdownWithLatex.web.css`) : même
 * chaîne — `react-markdown` + `remark-math` + `rehype-raw` + `rehype-sanitize` + `rehype-katex`,
 * dans cet ordre — même normalisation avant KaTeX (`normalizeEnonceLatex` sur la source,
 * `remarkEnonceLatex` sur l'arbre, partagés avec le legacy et le BO), même allowlist de classes,
 * même feuille KaTeX, mêmes polices KaTeX et PT Serif inlinées, chargées à la demande
 * (`richTextVendorCss`). Ce qui change :
 * l'habillage vient des jetons (`richTextStyles`), et aucun chrome legacy n'entre ici — une erreur
 * de rendu retombe sur le texte brut, pas sur un `<pre>` rouge ni sur un `notifyBugsnag`.
 *
 * `variant="message"` (une bulle de conversation) écrit en Outfit, les faces que l'app charge déjà
 * dans le document (`theme/fonts`), et ne pose pas les repères d'un énoncé (`markEnonceStructure`).
 *
 * L'ordre des greffons n'est pas décoratif : `rehype-raw` déplie le HTML du markdown,
 * `rehype-sanitize` le nettoie, PUIS `rehype-katex` compose — sinon son propre MathML se ferait
 * dépouiller. Les marqueurs `math` / `math-inline` / `math-display` que pose `remark-math`
 * doivent donc survivre à l'assainissement.
 */
import React, { useEffect, useMemo, useState } from "react"
import { View, type StyleProp, type ViewStyle } from "react-native"
import { useUnistyles } from "react-native-unistyles"
import ReactMarkdown from "react-markdown"
import remarkMath from "remark-math"
import rehypeKatex from "rehype-katex"
import rehypeRaw from "rehype-raw"
import rehypeSanitize, { defaultSchema } from "rehype-sanitize"

import { useHighlighterBand } from "../../atoms/Highlight/useHighlighterBand"
import { Text } from "../../atoms/typography/Text"
import { injectStyleOnce } from "../../utils/injectStyleOnce"
import { ImageDisplay } from "../ImageDisplay"
import { normalizeEnonceLatex, remarkEnonceLatex } from "./enonceLatex"
import { markEnonceStructure } from "./enonceStructure"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { ensureRichTextVendorCss } from "./richTextVendorCss"
import {
    ENONCE_CLASSES,
    RICH_TEXT_CSS,
    richTextCssVars,
    richTextRootClass,
    type RichTextReading,
    type RichTextVariant,
} from "./richTextStyles"

/**
 * Les cinq classes de la palette, et rien d'autre : le tuteur et l'IA peuvent teinter un mot sans
 * qu'une surface d'injection CSS s'ouvre. `style` reste bloqué ; `defaultSchema` retire déjà
 * `<script>`, les gestionnaires `on*` et les URL `javascript:`.
 *
 * L'expression se CONSTRUIT sur `ENONCE_CLASSES` : c'est la feuille qui dit quelles classes
 * existent, et autoriser ici une classe qu'elle n'habille pas n'aurait aucun sens.
 */
const ENONCE_CLASSNAME_PATTERN = new RegExp(`^enonce-(${ENONCE_CLASSES.join("|")})$`)
const MATH_CLASSNAME_PATTERN = /^math(-(inline|display))?$/

/**
 * Le schéma du legacy, à l'identique (`tutor/home/MarkdownWithLatex.web.js`). `remark-math` pose
 * ses marqueurs sur le `<code>` qu'il produit (`class="language-math math-display"`) : `code`
 * reprend donc la classe `language-*` du défaut ET les marqueurs de formule, dans UNE entrée —
 * `hast-util-sanitize` ne lit que la première définition d'une propriété. Sans `math-display`,
 * un `$$…$$` écrit dans une ligne sortait en ligne sur le web quand le natif le centre.
 */
const SANITIZE_SCHEMA = {
    ...defaultSchema,
    tagNames: [...(defaultSchema.tagNames ?? []), "figure", "figcaption"],
    attributes: {
        ...(defaultSchema.attributes ?? {}),
        code: [["className", /^language-./, MATH_CLASSNAME_PATTERN]],
        span: [
            ...(defaultSchema.attributes?.span ?? []),
            ["className", ENONCE_CLASSNAME_PATTERN, MATH_CLASSNAME_PATTERN],
        ],
        div: [
            ...(defaultSchema.attributes?.div ?? []),
            ["className", ENONCE_CLASSNAME_PATTERN, MATH_CLASSNAME_PATTERN],
        ],
    },
}

/**
 * La feuille de l'énoncé entre dans le document UNE fois, pas à chaque montage, après celle de KaTeX
 * — ses polices (0,55 Mo inlinés) arrivent à part, à la demande : `richTextVendorCss`.
 */
const STYLE_TAG_ID = "masteur-rich-text-styles"

function ensureRichTextStyles() {
    void ensureRichTextVendorCss()
    injectStyleOnce(STYLE_TAG_ID, RICH_TEXT_CSS)
}

/**
 * Les greffons sont des CONSTANTES de module : écrits dans le JSX, les deux tableaux sont neufs à
 * chaque rendu, et `react-markdown` rejoue alors toute sa chaîne unified pour un énoncé qui n'a
 * pas bougé.
 */
const REMARK_PLUGINS = [remarkMath, remarkEnonceLatex]
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const REHYPE_PLUGINS = [rehypeRaw, [rehypeSanitize, SANITIZE_SCHEMA], rehypeKatex] as any

/**
 * `react-markdown` enveloppe le contenu en ligne dans un `<p>`, donc un `![](url)` seul sur sa
 * ligne sort en `<p><figure>…</figure></p>` — HTML invalide. On déplie le `<p>` quand son unique
 * enfant est une image.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ParagraphRenderer({ node, children, ...rest }: any) {
    const onlyChild = node?.children?.length === 1 ? node.children[0] : null
    const isLoneImage = onlyChild?.type === "element" && onlyChild?.tagName === "img"
    if (isLoneImage) return children
    return <p {...rest}>{children}</p>
}

type RichTextImageProps = { alt?: string; src?: string; onZoom: (uri: string) => void }

function RichTextImage(props: RichTextImageProps) {
    const { alt, src, onZoom } = props
    const [broken, setBroken] = useState(false)

    if (broken) {
        return (
            <figure>
                <div
                    className="masteur-rich-text-broken"
                    role="img"
                    aria-label={alt !== "" ? alt : "image indisponible"}
                >
                    Image indisponible{alt != null && alt !== "" ? ` — ${alt}` : ""}
                </div>
            </figure>
        )
    }

    return (
        <figure>
            <img
                src={src}
                alt={alt ?? ""}
                loading="lazy"
                onClick={() => (src != null ? onZoom(src) : undefined)}
                onError={() => setBroken(true)}
            />
            {/* L'alt générique « figure » (pipeline d'incrustation) n'est pas une légende — ne
                l'afficher que s'il décrit vraiment l'image. */}
            {alt != null && alt !== "" && alt.toLowerCase() !== "figure" ? <figcaption>{alt}</figcaption> : null}
        </figure>
    )
}

/** Un rendu qui échoue rend le texte brut : on ne perd jamais l'énoncé pour une balise de travers. */
class RichTextBoundary extends React.Component<
    { fallback: React.ReactNode; children: React.ReactNode },
    { failed: boolean }
> {
    state = { failed: false }

    static getDerivedStateFromError() {
        return { failed: true }
    }

    render() {
        return this.state.failed ? this.props.fallback : this.props.children
    }
}

export type RichTextProps = {
    /** Le markdown, formules comprises. */
    value: string
    /** L'encre du texte, formules comprises. Défaut `text-primary`. */
    color?: SemanticColorKey
    /**
     * `start` : au début de la ligne, même posé dans un composant qui centre son texte (une réponse de
     * quiz, dans un bouton). Par défaut, l'alignement de l'endroit où il est posé.
     */
    align?: "start"
    /** Sans la marge de la feuille : le web n'en pose pas autour du texte, l'option ne change rien ici. */
    compact?: boolean
    style?: StyleProp<ViewStyle>
    testID?: string
} & (
    | {
          /** Un énoncé (défaut) : la feuille d'exercice, en PT Serif, avec ses repères. */
          variant?: "enonce"
          /** La lecture de l'élève (fiche « Ma lecture ») ; absente, la feuille standard. */
          reading?: RichTextReading
      }
    | {
          /** Un message : une bulle de conversation, en Outfit, sans les repères d'un énoncé. */
          variant: "message"
          reading?: never
      }
)

/** Le texte tel que le moteur le compose : un énoncé reçoit ses repères, un message reste tel quel. */
function composedSource(value: string, variant: RichTextVariant): string {
    return normalizeEnonceLatex(variant === "message" ? value : markEnonceStructure(value))
}

export function RichText(props: RichTextProps) {
    const { value, reading, variant = "enonce", color, align, style, testID } = props
    const { theme } = useUnistyles()
    const highlight = useHighlighterBand()
    const [viewerUri, setViewerUri] = useState<string | null>(null)

    useEffect(() => {
        ensureRichTextStyles()
    }, [])

    const components = useMemo(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        () => ({ img: (p: any) => <RichTextImage {...p} onZoom={setViewerUri} />, p: ParagraphRenderer }) as any,
        []
    )

    // C'est la composition du markdown qui coûte, pas la table de variables : l'élément
    // `<ReactMarkdown>` est mémoïsé sur l'énoncé, et les variables se recalculent librement.
    // Les mémoriser sur `theme` ne servait à rien — `useUnistyles()` rend un nouveau Proxy à
    // chaque rendu, donc la dépendance changeait toujours.
    const cssVars = richTextCssVars(theme, highlight, reading, variant, color, align)

    const markdown = useMemo(
        () => (
            <ReactMarkdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={REHYPE_PLUGINS} components={components}>
                {composedSource(value, variant)}
            </ReactMarkdown>
        ),
        [value, variant, components]
    )

    if (value === "") return null

    return (
        <View style={style} testID={testID}>
            <RichTextBoundary fallback={<Text>{value}</Text>}>
                <div className={richTextRootClass(reading, variant)} style={cssVars as React.CSSProperties}>
                    {markdown}
                </div>
            </RichTextBoundary>

            <ImageDisplay
                visible={viewerUri != null}
                images={viewerUri != null ? [{ uri: viewerUri }] : []}
                onClose={() => setViewerUri(null)}
            />
        </View>
    )
}
